import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { money, fmtDate } from "@/lib/format";
import { DateFilter, buildParams } from "@/components/DateFilter";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Wallet, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Expenses() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState({ period: "this_month" });
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState({ category: "", amount: "", payment_method: "cash", note: "", date: "" });

  const { data: cats } = useQuery({ queryKey: ["expense-cats"], queryFn: async () => (await api.get("/expense-categories")).data });
  const { data } = useQuery({ queryKey: ["expenses", filter], queryFn: async () => (await api.get("/expenses", { params: buildParams(filter) })).data });

  async function save() {
    try {
      await api.post("/expenses", { ...form, amount: Number(form.amount), date: form.date ? new Date(form.date).toISOString() : null });
      toast.success("Expense recorded"); setDialog(false); setForm({ category: "", amount: "", payment_method: "cash", note: "", date: "" });
      qc.invalidateQueries({ queryKey: ["expenses"] });
    } catch (e) { toast.error(apiError(e)); }
  }
  async function del(id) { try { await api.delete(`/expenses/${id}`); toast.success("Deleted"); qc.invalidateQueries({ queryKey: ["expenses"] }); } catch (e) { toast.error(apiError(e)); } }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h2 className="font-display text-2xl font-bold text-[#0F172A]">Expenses</h2><p className="text-slate-500 text-sm">Total: {money(data?.total)}</p></div>
        <div className="flex gap-2">
          <DateFilter value={filter} onChange={setFilter} />
          <Button className="bg-[#F4571E] hover:bg-[#D9420D] gap-2" onClick={() => setDialog(true)} data-testid="add-expense-btn"><Plus className="h-4 w-4" /> Add Expense</Button>
        </div>
      </div>

      <Card className="border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-slate-50"><TableHead>Date</TableHead><TableHead>Category</TableHead><TableHead>Note</TableHead><TableHead>Method</TableHead><TableHead className="text-right">Amount</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {(data?.items || []).length ? data.items.map((e) => (
                <TableRow key={e.id} className="hover:bg-slate-50">
                  <TableCell className="text-slate-500 text-sm">{fmtDate(e.date)}</TableCell>
                  <TableCell><Badge variant="secondary">{e.category}</Badge></TableCell>
                  <TableCell className="text-slate-600">{e.note || "—"}</TableCell>
                  <TableCell className="capitalize text-slate-500">{e.payment_method}</TableCell>
                  <TableCell className="text-right font-mono font-semibold">{money(e.amount)}</TableCell>
                  <TableCell className="text-right"><Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => del(e.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button></TableCell>
                </TableRow>
              )) : <TableRow><TableCell colSpan={6} className="text-center py-12 text-slate-400"><Wallet className="h-8 w-8 mx-auto mb-2 opacity-40" />No expenses</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-display text-[#1A2A5E]">Add Expense</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="mt-1" data-testid="expense-category"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="bg-white/95 backdrop-blur-md">{(cats || []).map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Amount</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1 font-mono" data-testid="expense-amount" /></div>
            <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1" /></div>
            <div><Label>Payment</Label>
              <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white/95 backdrop-blur-md">{["cash", "upi", "card", "bank"].map((m) => <SelectItem key={m} value={m} className="capitalize">{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Note</Label><Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="mt-1" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDialog(false)}>Cancel</Button><Button className="bg-[#1A2A5E]" disabled={!form.category || !form.amount} onClick={save} data-testid="save-expense">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
